<template>
  <div
    class=""
    :class="{ 'border-top my-3 mx-n4 pt-3 px-4': placement === 'footer' }"
  >
    <div class="pb-2" v-if="placement === 'footer'">
      Final Check! Confirm your Observation Start and End Date & Time
    </div>
    <div class="observation-time-flex d-flex flex-wrap">
      <div
        class="form-group observation-time-item observation-time-item--start"
        :class="{ 'observation-time-item--full': !showObservationEndFields }"
      >
        <label class="font-weight-bold">
          Observation
          <span v-if="showObservationEndFields" class=""> Start</span> Date
          <span v-if="includesTime"> & Time</span>
          <span class="required-asterisk"> *</span>
        </label>

        <div class="input-group split-datetime-input">
          <input
            class="form-control split-date"
            :class="{ 'is-invalid': !!visibleStartDateError }"
            type="date"
            :value="startDateInput"
            :max="dateTomorrow"
            ref="startDateInput"
            name="observationStartDate"
            data-vv-as="Observation Start Date"
            v-validate="startDateRules"
            data-vv-validate-on="change|blur"
            @focus="requestManualMode"
            @input="onObservationStartDateInput"
            @blur="onObservationStartDateBlur"
          />
          <div class="input-group-append" v-if="!isFirefox()">
            <button
              ref="startDatePickerButton"
              type="button"
              class="btn btn-secondary px-lg-4"
              @focus="requestManualMode"
              @click="onObservationStartDateClick"
              aria-label="Open start date picker"
            >
              <i class="far fa-calendar-alt fa-lg"></i>
            </button>
          </div>
          <input
            v-if="includesTime"
            ref="startTimeInput"
            class="form-control split-time"
            :class="{ 'is-invalid': !!visibleStartTimeError }"
            type="time"
            :step="timeStep"
            :value="startTimeInput"
            name="observationStartTime"
            data-vv-as="Observation Start Time"
            v-validate="startTimeRules"
            data-vv-validate-on="change|blur"
            @focus="requestManualMode"
            @input="onObservationStartTimeInput"
          />
          <div class="input-group-append" v-if="includesTime">
            <b-form-timepicker
              ref="startTimeButtonBV"
              v-if="shouldUseBootstrapTimePicker()"
              button-only
              now-button
              right
              :show-seconds="observationTimeUsesSeconds"
              :value="startTimeInput"
              button-variant="secondary"
              @shown="requestManualMode"
              @input="onObservationStartTimePickerInput"
            />
            <button
              v-else
              ref="startTimeButtonReg"
              type="button"
              class="btn btn-secondary px-lg-4"
              @focus="requestManualMode"
              @click="onObservationStartTimeButtonClick($event)"
              aria-label="Open start time picker"
            >
              <i class="far fa-clock fa-lg"></i>
            </button>
          </div>
        </div>
        <label v-if="startDateTimeMessage" :class="startDateTimeMessageClass">
          {{ startDateTimeMessage }}
        </label>
        <label v-if="startDateTimeWarning" class="text-warning d-block">
          {{ startDateTimeWarning }}
        </label>
      </div>

      <div
        class="form-group observation-time-item observation-time-item--duration"
        v-if="showObservationEndFields"
      >
        <label for="observation-duration-html5" class="font-weight-bold">
          Duration<span class="required-asterisk"> *</span>
        </label>
        <div class="input-group">
          <input
            id="observation-duration-html5"
            :class="{ 'observation-time-item--running': mode === 'automatic' }"
            class="form-control"
            :value="observationDurationInputValue"
            type="text"
            inputmode="decimal"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            @focus="onObservationDurationFocus"
            @blur="onObservationDurationBlur"
            @input="onObservationDurationInput"
          />
          <div class="input-group-append">
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="changeDurationUnit"
            >
              {{ durationUnit }}
            </button>
          </div>
        </div>
      </div>

      <div
        class="form-group observation-time-item observation-time-item--end"
        v-if="showObservationEndFields"
      >
        <label class="font-weight-bold">
          Observation End Date & Time<span class="required-asterisk"> *</span>
          <a
            href="https://blog.citsci.org/2026/05/12/tracking-time-volunteers-spend-collecting-citsci-data/"
            target="_blank"
            rel="noopener noreferrer"
            class="ml-2 px-1"
          >
            <i class="fas fa-info-circle"></i>
          </a>
        </label>

        <div class="input-group split-datetime-input">
          <input
            class="form-control split-date"
            :class="{ 'is-invalid': !!visibleEndDateError }"
            type="date"
            :value="endDateInput"
            :min="endMinDate"
            :max="dateTomorrow"
            ref="endDateInput"
            name="observationEndDate"
            data-vv-as="Observation End Date"
            v-validate="endDateRules"
            data-vv-validate-on="change|blur"
            @focus="requestManualMode"
            @input="onObservationEndDateInput"
            @blur="onObservationEndDateBlur"
          />
          <div class="input-group-append" v-if="!isFirefox()">
            <button
              ref="endDatePickerButton"
              type="button"
              class="btn btn-secondary px-lg-4"
              @focus="requestManualMode"
              @click="onObservationEndDateClick"
              aria-label="Open end date picker"
            >
              <i class="far fa-calendar-alt fa-lg"></i>
            </button>
          </div>
          <input
            class="form-control split-time"
            :class="{
              'observation-time-item--running': mode === 'automatic',
              'is-invalid': !!visibleEndTimeError,
            }"
            type="time"
            :step="timeStep"
            :value="endTimeInput"
            ref="endTimeInput"
            name="observationEndTime"
            data-vv-as="Observation End Time"
            v-validate="endTimeRules"
            data-vv-validate-on="change|blur"
            @focus="requestManualMode"
            @input="onObservationEndTimeInput"
          />
          <div class="input-group-append">
            <b-form-timepicker
              ref="endTimeButtonBV"
              v-if="shouldUseBootstrapTimePicker()"
              button-only
              now-button
              right
              :show-seconds="observationTimeUsesSeconds"
              :value="endTimeInput"
              button-variant="secondary"
              @shown="onObservationEndTimePickerShown"
              @input="onObservationEndTimePickerInput"
            />
            <button
              v-else
              ref="endTimeButtonReg"
              type="button"
              class="btn btn-secondary px-lg-4"
              @focus="requestManualMode"
              @click="onObservationEndTimeButtonClick($event)"
              aria-label="Open end time picker"
            >
              <i class="far fa-clock fa-lg"></i>
            </button>
          </div>
        </div>
        <label v-if="endDateTimeMessage" :class="endDateTimeMessageClass">
          {{ endDateTimeMessage }}
        </label>
        <label v-if="endDateTimeWarning" class="text-warning d-block">
          {{ endDateTimeWarning }}
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import moment, { Moment } from "moment";

export default {
  name: "ObservationDateTimeHTML5",
  inject: ["parentValidator"],
  props: {
    placement: {
      type: String,
      default: "header",
      validator: (value) => ["header", "footer"].includes(value),
    },
    dateFormat: {
      type: String,
      default: "start_end_minutes",
      validator: (value) =>
        [
          "start_end_minutes",
          "start_end_seconds",
          "date",
          "date_hour_minutes",
          "datetime",
        ].includes(value),
    },
    mode: {
      type: String,
      default: "manual",
      validator: (value) => ["manual", "automatic"].includes(value),
    },
    durationUnit: {
      type: String,
      default: "minutes",
      validator: (value) => ["minutes", "hours"].includes(value),
    },
    observationStart: {
      type: Moment,
      default: null,
    },
    observationEnd: {
      type: Moment,
      default: null,
    },
  },
  data() {
    return {
      observationDurationDraft: null,
      lastObservationDurationEmittedValue: null,
      isObservationDurationFocused: false,
      startDateInput: "",
      startTimeInput: "",
      endDateInput: "",
      endTimeInput: "",
      nowTimestampMs: Date.now(),
      nowTickerId: null,
    };
  },
  created() {
    if (this.parentValidator) {
      this.$validator = this.parentValidator;
    }
  },
  computed: {
    showObservationEndFields() {
      return ["start_end_minutes", "start_end_seconds"].includes(
        this.dateFormat,
      );
    },
    includesTime() {
      return this.dateFormat !== "date";
    },
    observationTimeUsesSeconds() {
      return ["datetime", "start_end_seconds"].includes(this.dateFormat);
    },
    timeStep() {
      return this.observationTimeUsesSeconds ? "1" : "60";
    },
    dateTomorrow() {
      return moment().add(1, "day").format("YYYY-MM-DD");
    },
    endMinDate() {
      return this.startDateInput || "";
    },
    startDateRules() {
      return `required|date_format:yyyy-MM-dd|date_between:1900-01-01,${this.dateTomorrow},true`;
    },
    endDateRules() {
      const minDate = this.startDateInput || "1900-01-01";
      return `required|date_format:yyyy-MM-dd|date_between:${minDate},${this.dateTomorrow},true`;
    },
    startTimeRules() {
      if (!this.includesTime) {
        return "";
      }

      return `required|time_format:${this.observationTimeUsesSeconds ? "seconds" : "minutes"}`;
    },
    endTimeRules() {
      if (!this.showObservationEndFields) {
        return "";
      }

      return `required|time_format:${this.observationTimeUsesSeconds ? "seconds" : "minutes"}`;
    },
    observationDuration() {
      if (!this.observationStart || !this.observationEnd) {
        return null;
      }
      const start = moment.isMoment(this.observationStart)
        ? this.observationStart
        : moment(this.observationStart);
      const end = moment.isMoment(this.observationEnd)
        ? this.observationEnd
        : moment(this.observationEnd);
      const durationSeconds = end.diff(start, "seconds", true);
      if (Number.isNaN(durationSeconds) || durationSeconds < 0) {
        return null;
      }
      const durationMinutes = durationSeconds / 60;
      if (this.durationUnit === "hours") {
        return Number((durationMinutes / 60).toFixed(2));
      }
      return Math.round(durationMinutes * 100) / 100;
    },
    observationDurationInputValue() {
      return this.observationDurationDraft !== null
        ? this.observationDurationDraft
        : this.observationDuration;
    },
    startMomentFromInputs() {
      return this.parseDateTime(this.startDateInput, this.startTimeInput);
    },
    endMomentFromInputs() {
      return this.parseDateTime(this.endDateInput, this.endTimeInput);
    },

    visibleStartDateError() {
      return this.errors.first("observationStartDate");
    },
    visibleStartTimeError() {
      return this.errors.first("observationStartTime");
    },
    visibleEndDateError() {
      return this.errors.first("observationEndDate");
    },
    visibleEndTimeError() {
      return this.errors.first("observationEndTime");
    },
    startDateTimeMessage() {
      return this.visibleStartDateError || this.visibleStartTimeError;
    },
    startDateTimeMessageClass() {
      return "text-danger";
    },
    endDateTimeMessage() {
      return (
        this.visibleEndDateError ||
        this.visibleEndTimeError ||
        this.getChronologyError()
      );
    },
    endDateTimeMessageClass() {
      return "text-danger";
    },
    startDateTimeWarning() {
      if (
        !this.startMomentFromInputs ||
        !this.startMomentFromInputs.isValid()
      ) {
        return null;
      }

      if (
        this.startMomentFromInputs.isAfter(this.futureWarningThresholdMoment)
      ) {
        return "Observation Start is in the future.";
      }

      return null;
    },
    endDateTimeWarning() {
      if (!this.showObservationEndFields) {
        return null;
      }

      if (!this.endMomentFromInputs || !this.endMomentFromInputs.isValid()) {
        return null;
      }

      if (this.endMomentFromInputs.isAfter(this.futureWarningThresholdMoment)) {
        return "Observation End is in the future.";
      }

      return null;
    },
    nowMoment() {
      return moment(this.nowTimestampMs);
    },
    futureWarningThresholdMoment() {
      return this.nowMoment.clone().add(1, "minute");
    },
  },
  mounted() {
    this.nowTickerId = window.setInterval(() => {
      this.nowTimestampMs = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    if (this.nowTickerId) {
      window.clearInterval(this.nowTickerId);
      this.nowTickerId = null;
    }
  },
  watch: {
    observationStart: {
      immediate: true,
      handler(value) {
        this.syncStartInputsFromValue(value);
      },
    },
    observationEnd: {
      immediate: true,
      handler(value) {
        this.syncEndInputsFromValue(value);
      },
    },
  },
  methods: {
    async validate() {
      const valid = await this.$validator.validateAll();
      const hasTimeErrors = Boolean(
        this.errors.first("observationStartDate") ||
        this.errors.first("observationStartTime") ||
        this.errors.first("observationEndDate") ||
        this.errors.first("observationEndTime")
      );
      const chronologyError = this.getChronologyError();
      return !hasTimeErrors && !chronologyError;
    },
    sanitizeDecimalInput(value) {
      const sanitized = String(value).replace(/[^0-9.]/g, "");
      const firstDotIndex = sanitized.indexOf(".");

      if (firstDotIndex === -1) {
        return sanitized;
      }

      return (
        sanitized.slice(0, firstDotIndex + 1) +
        sanitized.slice(firstDotIndex + 1).replace(/\./g, "")
      );
    },
    normalizeObservationMoment(value) {
      const normalized = moment.isMoment(value) ? value.clone() : moment(value);

      if (!normalized.isValid()) {
        return normalized;
      }

      if (!this.observationTimeUsesSeconds) {
        normalized.seconds(0).milliseconds(0);
      }

      return normalized;
    },
    syncStartInputsFromValue(value) {
      if (!value) {
        this.startDateInput = "";
        this.startTimeInput = "";
        return;
      }
      const m = this.normalizeObservationMoment(value);
      if (!m.isValid()) {
        this.startDateInput = "";
        this.startTimeInput = "";
        return;
      }
      this.startDateInput = m.format("YYYY-MM-DD");
      this.startTimeInput = this.observationTimeUsesSeconds
        ? m.format("HH:mm:ss")
        : m.format("HH:mm");
    },
    syncEndInputsFromValue(value) {
      if (!value) {
        this.endDateInput = "";
        this.endTimeInput = "";
        return;
      }
      const m = this.normalizeObservationMoment(value);
      if (!m.isValid()) {
        this.endDateInput = "";
        this.endTimeInput = "";
        return;
      }
      this.endDateInput = m.format("YYYY-MM-DD");
      this.endTimeInput = this.observationTimeUsesSeconds
        ? m.format("HH:mm:ss")
        : m.format("HH:mm");
    },
    parseDateTime(dateValue, timeValue) {
      if (!dateValue) {
        return null;
      }

      if (!this.includesTime) {
        const parsedDateOnly = this.normalizeObservationMoment(
          moment(dateValue, "YYYY-MM-DD", true),
        );
        return parsedDateOnly.isValid() ? parsedDateOnly : null;
      }

      if (!timeValue) {
        return null;
      }

      const format = this.observationTimeUsesSeconds
        ? "YYYY-MM-DD HH:mm:ss"
        : "YYYY-MM-DD HH:mm";
      const parsed = this.normalizeObservationMoment(
        moment(`${dateValue} ${timeValue}`, format, true),
      );
      return parsed.isValid() ? parsed : null;
    },
    normalizeTimeInputValue(value) {
      if (!value) {
        return "";
      }

      const normalizedValue = String(value)
        .trim()
        .replace(/\.\d+$/, "");

      if (this.observationTimeUsesSeconds) {
        if (/^\d{1,2}:\d{2}$/.test(normalizedValue)) {
          return `${normalizedValue}:00`;
        }

        const withSecondsMatch = normalizedValue.match(
          /^(\d{1,2}:\d{2}:\d{2})/,
        );
        return withSecondsMatch ? withSecondsMatch[1] : normalizedValue;
      }

      const minutesMatch = normalizedValue.match(/^(\d{1,2}:\d{2})/);
      return minutesMatch ? minutesMatch[1] : normalizedValue;
    },
    getChronologyError() {
      if (!this.showObservationEndFields) {
        return null;
      }

      if (!this.startMomentFromInputs || !this.endMomentFromInputs) {
        return null;
      }

      if (
        this.startMomentFromInputs.isValid() &&
        this.endMomentFromInputs.isValid() &&
        this.endMomentFromInputs.isBefore(this.startMomentFromInputs)
      ) {
        return "Observation End must be after Observation Start.";
      }

      return null;
    },
    onObservationStartDateInput(event) {
      this.startDateInput = event.target.value;
      this.emitObservationStartFromInputs();
    },
    onObservationStartDateBlur(event) {
      const relatedTarget = event ? event.relatedTarget : null;
      const startTimeButtonBV =
        this.getBootstrapTimepickerButton("startTimeButtonBV");

      // dont show picker if the new focused element is the time input of the same observation, this allows seamless transition from date to time input without showing the date picker again
      if (
        relatedTarget === this.$refs.startDatePickerButton ||
        relatedTarget === this.$refs.startTimeInput ||
        relatedTarget === this.$refs.startTimeButtonReg ||
        (startTimeButtonBV && relatedTarget === startTimeButtonBV)
      ) {
        return;
      }
      this.showAssociatedTimePicker("startTimeInput", true);
    },
    onObservationStartDateClick() {
      this.showAssociatedDatePicker("startDateInput");
    },
    onObservationStartTimeInput(event) {
      const normalizedTime = this.normalizeTimeInputValue(event.target.value);
      if (normalizedTime !== event.target.value) {
        event.target.value = normalizedTime;
      }
      this.startTimeInput = normalizedTime;
      this.emitObservationStartFromInputs();
    },
    onObservationStartTimePickerInput(value) {
      this.requestManualMode();
      this.startTimeInput = this.normalizeTimeInputValue(value);
      this.emitObservationStartFromInputs();
      this.$nextTick(() => {
        this.$validator.validate("observationStartTime");
      });
    },
    onObservationStartTimeButtonClick() {
      this.showAssociatedTimePicker("startTimeInput");
    },
    emitObservationStartFromInputs() {
      const start = this.parseDateTime(
        this.startDateInput,
        this.startTimeInput,
      );

      if (!start) {
        if (!this.startDateInput) {
          this.$emit("update:start", null);
          this.$emit("start-input");
        }
        return;
      }

      if (this.showObservationEndFields && !this.endDateInput) {
        this.endDateInput = this.startDateInput;
      }

      if (
        this.showObservationEndFields &&
        this.observationStart &&
        this.observationEnd
      ) {
        const previousStart = this.normalizeObservationMoment(
          this.observationStart,
        );
        const previousEnd = this.normalizeObservationMoment(
          this.observationEnd,
        );

        if (previousStart.isValid() && previousEnd.isValid()) {
          const durationMs = previousEnd.diff(previousStart, "milliseconds");
          if (durationMs >= 0) {
            const newEnd = moment(start).add(durationMs, "milliseconds");
            this.$emit("update:end", newEnd.toISOString());
          }
        }
      }

      this.$emit("update:start", start.toISOString());
      this.$emit("start-input");
    },
    onObservationEndDateInput(event) {
      this.endDateInput = event.target.value;
      this.emitObservationEndFromInputs();
    },
    onObservationEndDateBlur(event) {
      const relatedTarget = event ? event.relatedTarget : null;
      const endTimeButtonBV =
        this.getBootstrapTimepickerButton("endTimeButtonBV");

      // show picker, if the user did not click on the picker button but left the field
      if (
        relatedTarget === this.$refs.endDatePickerButton ||
        relatedTarget === this.$refs.endTimeInput ||
        relatedTarget === this.$refs.endTimeButtonReg ||
        (endTimeButtonBV && relatedTarget === endTimeButtonBV)
      ) {
        return;
      }
      this.showAssociatedTimePicker("endTimeInput", true);
    },
    onObservationEndDateClick() {
      this.showAssociatedDatePicker("endDateInput");
    },
    onObservationEndTimeInput(event) {
      const normalizedTime = this.normalizeTimeInputValue(event.target.value);
      if (normalizedTime !== event.target.value) {
        event.target.value = normalizedTime;
      }
      this.endTimeInput = normalizedTime;
      this.emitObservationEndFromInputs();
    },
    onObservationEndTimePickerInput(value) {
      this.requestManualMode();
      this.endTimeInput = this.normalizeTimeInputValue(value);
      this.emitObservationEndFromInputs();
      this.$nextTick(() => {
        this.$validator.validate("observationEndTime");
      });
    },
    onObservationEndTimeButtonClick(event) {
      this.showAssociatedTimePicker("endTimeInput");
    },
    onObservationEndTimePickerShown() {
      this.requestManualMode();
      this.ensureEndTimeFromStartIfEmpty();
    },
    showAssociatedDatePicker(refName) {
      const dateInput = this.$refs[refName];
      if (!dateInput) {
        return;
      }

      this.$nextTick(() => {
        try {
          dateInput.focus();
          if (typeof dateInput.showPicker === "function") {
            dateInput.showPicker();
          }
        } catch (e) {
          // ignore, fallback to focus should still allow user to open picker manually
        }
      });
    },
    showAssociatedTimePicker(refName, deferBootstrapClick = false) {
      if (!this.includesTime) {
        return;
      }

      const timeInput = this.$refs[refName];
      if (!timeInput) {
        return;
      }

      if (this.shouldUseBootstrapTimePicker()) {
        if (deferBootstrapClick) {
          window.setTimeout(() => {
            this.showBootstrapTimePicker(refName);
          }, 500);
        } else {
          this.showBootstrapTimePicker(refName);
        }
        return;
      }
      timeInput.focus();
      try {
        if (typeof timeInput.showPicker === "function") {
          timeInput.showPicker();
        }
      } catch (e) {
        // ignore
      }
      return;
    },
    showBootstrapTimePicker(refName) {
      const picker =
        this.$refs[
          refName === "startTimeInput" ? "startTimeButtonBV" : "endTimeButtonBV"
        ];

      if (!picker || !picker.$el) {
        return;
      }

      const button = picker.$el.querySelector("button");
      if (button && typeof button.click === "function") {
        button.click();
        return;
      }
    },
    getBootstrapTimepickerButton(refName) {
      const picker = this.$refs[refName];
      if (!picker || !picker.$el) {
        return null;
      }

      return picker.$el.querySelector("button");
    },
    isFirefox() {
      return (
        typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent)
      );
    },
    isFirefoxAndroid() {
      if (!this.isFirefox() || typeof navigator === "undefined") {
        return false;
      }

      const userAgent = navigator.userAgent || "";
      return /Android/i.test(userAgent);
    },
    isFirefoxDesktop() {
      return this.isFirefox() && !this.isFirefoxAndroid();
    },
    isSafari() {
      if (typeof navigator === "undefined") {
        return false;
      }

      const userAgent = navigator.userAgent || "";
      return (
        /safari/i.test(userAgent) &&
        !/chrome|crios|chromium|android|fxios|edgios/i.test(userAgent)
      );
    },
    isIOS() {
      if (typeof navigator === "undefined") {
        return false;
      }

      const userAgent = navigator.userAgent || "";
      const platform = navigator.platform || "";
      const maxTouchPoints = navigator.maxTouchPoints || 0;

      return (
        /iPad|iPhone|iPod/i.test(userAgent) ||
        (platform === "MacIntel" && maxTouchPoints > 1)
      );
    },
    isMacSafari() {
      if (!this.isSafari() || this.isIOS()) {
        return false;
      }

      const userAgent = navigator.userAgent || "";
      return /Macintosh|Mac OS X/i.test(userAgent);
    },
    // Use Bootstrap timepicker where native UX is missing/incomplete:
    // - Firefox: no native popup support parity
    // - macOS Safari: no popup time picker
    // - iOS Safari: no seconds support when seconds are required
    shouldUseBootstrapTimePicker() {
      if (this.isFirefoxAndroid()) {
        return false;
      }

      if (this.isFirefoxDesktop()) {
        return true;
      }

      if (this.isMacSafari()) {
        return true;
      }

      return this.isSafari() && this.isIOS() && this.observationTimeUsesSeconds;
    },
    emitObservationEndFromInputs() {
      const end = this.parseDateTime(this.endDateInput, this.endTimeInput);

      if (!end) {
        if (!this.endDateInput) {
          this.$emit("update:end", null);
          this.$emit("end-input");
        }
        return;
      }

      this.$emit("update:end", end.toISOString());
      this.$emit("end-input");
    },
    onObservationDurationInput(event) {
      const value = this.sanitizeDecimalInput(event.target.value).trim();
      event.target.value = value;
      this.observationDurationDraft = value;

      this.emitObservationDurationIfValid(value);
    },
    onObservationDurationFocus() {
      this.requestManualMode();
      this.isObservationDurationFocused = true;

      if (this.observationDurationDraft === null) {
        if (this.mode === "automatic") {
          this.observationDurationDraft = "";
          return;
        }

        this.observationDurationDraft =
          this.observationDuration !== null &&
          this.observationDuration !== undefined
            ? String(this.observationDuration)
            : "";
      }
    },
    onObservationDurationBlur() {
      this.isObservationDurationFocused = false;
      this.observationDurationDraft = null;
    },
    requestManualMode() {
      this.$emit("switch-to-manual");
    },
    ensureEndTimeFromStartIfEmpty() {
      if (!this.endTimeInput && this.startTimeInput) {
        this.endTimeInput = this.startTimeInput;
      }
    },
    emitObservationDurationIfValid(value) {
      if (value === this.lastObservationDurationEmittedValue) {
        return;
      }

      if (value === "") {
        this.lastObservationDurationEmittedValue = null;
        this.observationDurationDraft = this.isObservationDurationFocused
          ? ""
          : null;
        this.$emit("update:end", null);
        this.$emit("duration-input");
        return;
      }

      const numericValue = Number(value);
      if (Number.isNaN(numericValue) || numericValue < 0) {
        return;
      }

      let startMoment;
      if (moment.isMoment(this.observationStart)) {
        startMoment = this.normalizeObservationMoment(this.observationStart);
      } else if (typeof this.observationStart === "string") {
        startMoment = this.normalizeObservationMoment(this.observationStart);
      } else {
        return;
      }

      if (!startMoment.isValid()) {
        return;
      }

      const durationMinutes =
        this.durationUnit === "hours" ? numericValue * 60 : numericValue;
      const endMoment = moment(startMoment).add(durationMinutes, "minutes");

      this.lastObservationDurationEmittedValue = value;
      this.observationDurationDraft = this.isObservationDurationFocused
        ? value
        : null;
      this.$emit("update:start", startMoment.toISOString());
      this.$emit("update:end", endMoment.toISOString());
      this.$emit("duration-input");
      this.$nextTick(() => {
        this.$validator.validate("observationEndDate");
        this.$validator.validate("observationEndTime");
      });
    },
    changeDurationUnit() {
      this.$emit("change-duration-unit");
    },
  },
};
</script>

<style scoped>
.required-asterisk {
  color: #dc3545;
}

input[type="date"]::-webkit-calendar-picker-indicator,
input[type="time"]::-webkit-calendar-picker-indicator {
  display: none;
  -webkit-appearance: none;
}

/* Firefox-specific fallback attempts for native date/time affordance */
input[type="date"]::-moz-calendar-picker-indicator,
input[type="time"]::-moz-calendar-picker-indicator {
  display: none;
}

input[type="date"],
input[type="time"] {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: textfield;
  background-image: none;
}

.split-datetime-input .split-date {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.split-datetime-input .split-time {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.observation-time-flex {
  margin-left: -0.5rem !important;
  margin-right: -0.5rem !important;
}

.observation-time-item {
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
  flex: 1 1 280px !important;
  min-width: 240px !important;
}

.observation-time-item--start,
.observation-time-item--end {
  flex: 2 1 350px !important;
  min-width: 300px !important;
  max-width: 75%;
}

.observation-time-item--duration {
  flex: 0 1 170px !important;
  min-width: 120px !important;
  max-width: 75% !important;
}

.observation-time-item--running {
  border: 1.5px solid #00aaef !important;
}

@media (max-width: 599.98px) {
  .observation-time-item,
  .observation-time-item--start,
  .observation-time-item--duration,
  .observation-time-item--end {
    flex: 1 1 100% !important;
    max-width: 100% !important;
  }
}
</style>
